<script setup lang="ts">
import {
    messageRegister,
    messageRegisterEmpty,
    showMessageRegister
} from '@/event/account/alert/messageRegister'
import { AlertCircle, CheckCircle2, X } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const { t, te } = useI18n()

// Translate a backend key if it exists in the i18n catalog,
// otherwise return the raw string (fallback for legacy messages).
function tr(key: string): string {
    if (!key) return ''
    return te(key) ? t(key) : key
}

function toggleAlertRegisterHappy() {
    showMessageRegister.value = false
    setTimeout(() => {
        messageRegister.value = messageRegisterEmpty
    }, 300)
}
</script>

<template>
    <section class="section-message-alert-register-happy">
        <div
            class="div-overlay-register-alert-happy"
            @click="toggleAlertRegisterHappy"
        ></div>

        <Transition
            name="transition-message-register"
            enter-active-class="transition-all duration-300 ease-out"
            leave-active-class="transition-all duration-300 ease-in"
            enter-from-class="opacity-0 translate-y-4"
            enter-to-class="opacity-100 translate-y-0"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-4"
            :key="'MessageRegister'"
        >
            <!-- Variant: with errors list -->
            <div
                v-if="
                    messageRegister.errors &&
                    messageRegister.errors.length > 0 &&
                    showMessageRegister
                "
                class="alert-card"
                :class="messageRegister.class"
            >
                <div class="alert-header">
                    <div class="alert-header-left">
                        <AlertCircle
                            v-if="messageRegister.class === 'error'"
                            class="alert-icon"
                        />
                        <CheckCircle2
                            v-else-if="messageRegister.class === 'success'"
                            class="alert-icon"
                        />
                        <span class="alert-title">
                            {{ tr(messageRegister.message) || t('missingFields') }}
                        </span>
                    </div>
                    <button
                        type="button"
                        class="alert-close"
                        @click="toggleAlertRegisterHappy"
                    >
                        <X class="h-4 w-4" />
                    </button>
                </div>
                <ul class="alert-list">
                    <li
                        v-for="(error, index) in messageRegister.errors"
                        :key="index"
                        class="alert-list-item"
                    >
                        <span class="alert-bullet"></span>
                        <span>{{ tr(error) }}</span>
                    </li>
                </ul>
            </div>

            <!-- Variant: single message only -->
            <div
                v-else-if="
                    messageRegister.message &&
                    showMessageRegister &&
                    (!messageRegister.errors || messageRegister.errors.length === 0)
                "
                class="alert-card"
                :class="messageRegister.class"
            >
                <div class="alert-header">
                    <div class="alert-header-left">
                        <AlertCircle
                            v-if="messageRegister.class === 'error'"
                            class="alert-icon"
                        />
                        <CheckCircle2
                            v-else-if="messageRegister.class === 'success'"
                            class="alert-icon"
                        />
                        <span class="alert-title">{{ tr(messageRegister.message) }}</span>
                    </div>
                    <button
                        type="button"
                        class="alert-close"
                        @click="toggleAlertRegisterHappy"
                    >
                        <X class="h-4 w-4" />
                    </button>
                </div>
            </div>
        </Transition>
    </section>
</template>

<style scoped>
.section-message-alert-register-happy {
    @apply fixed inset-0 z-20 pointer-events-none;
}

.div-overlay-register-alert-happy {
    @apply w-full h-full pointer-events-auto;
}

/* Card base */
.alert-card {
    @apply pointer-events-auto fixed bottom-4 left-2 right-2 z-10 overflow-hidden rounded-xl shadow-2xl ring-1;
    @apply sm:left-auto sm:right-4 sm:w-[22rem] sm:max-w-sm;
}

/* Error variant */
.alert-card.error {
    color: white;
    background-color: rgb(var(--t-danger) / 0.95);
    --tw-ring-color: rgb(var(--t-danger) / 0.6);
}
.alert-card.error .alert-header {
    background-color: rgb(var(--t-danger));
    border-bottom: 1px solid rgb(255 255 255 / 0.15);
}
.alert-card.error .alert-close:hover,
.alert-card.error .alert-close:focus {
    @apply bg-white/15;
}
.alert-card.error .alert-bullet {
    @apply bg-white/85;
}

/* Success variant */
.alert-card.success {
    color: white;
    background-color: rgb(var(--t-success) / 0.95);
    --tw-ring-color: rgb(var(--t-success) / 0.6);
}
.alert-card.success .alert-header {
    background-color: rgb(var(--t-success));
    border-bottom: 1px solid rgb(255 255 255 / 0.15);
}
.alert-card.success .alert-close:hover,
.alert-card.success .alert-close:focus {
    @apply bg-white/15;
}
.alert-card.success .alert-bullet {
    @apply bg-white/85;
}

/* Header */
.alert-header {
    @apply flex items-center justify-between gap-3 px-4 py-2.5;
}

.alert-header-left {
    @apply flex items-center gap-2 min-w-0 flex-1;
}

.alert-icon {
    @apply h-5 w-5 shrink-0;
}

.alert-title {
    @apply font-semibold text-sm truncate;
}

.alert-close {
    @apply shrink-0 p-1 rounded-md transition-colors outline-none;
    cursor: pointer;
}

/* List */
.alert-list {
    @apply px-4 py-3 space-y-1.5 text-sm;
}

.alert-list-item {
    @apply flex items-start gap-2.5;
}

.alert-bullet {
    @apply mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 opacity-80;
}
</style>
